import { getIdToken, getApiUrl } from "./auth";

export interface InsightData {
  headline: string;
  body: string;
  topic: string;
  count: number;
  since: number;
}

export async function getInsight(): Promise<InsightData | null> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();

  const res = await fetch(`${apiUrl}/insight`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) return null;
  const data = await res.json();
  return data.insight ?? null;
}

interface McpResponse {
  jsonrpc: "2.0";
  id: number;
  result?: {
    content?: Array<{ type: string; text: string }>;
    [key: string]: unknown;
  };
  error?: { code: number; message: string };
}

export async function callTool(
  toolName: string,
  args: Record<string, unknown> = {}
): Promise<string> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();

  const res = await fetch(`${apiUrl}/mcp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json, text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: 1,
      method: "tools/call",
      params: { name: toolName, arguments: args },
    }),
  });

  if (!res.ok) throw new Error(`API error: ${res.status}`);

  const json = (await res.json()) as McpResponse;
  if (json.error) throw new Error(json.error.message);

  const content = json.result?.content;
  if (content && content.length > 0) {
    return content.map((c) => c.text).join("\n");
  }

  return JSON.stringify(json.result, null, 2);
}

// ---------------------------------------------------------------------------
// Standup board (docs/standup/)
// ---------------------------------------------------------------------------

/**
 * A focus IS the focus pin (D1-c §3.3). It pins onto a register row rather than
 * copying it: `actionId` / `issueKey` are what a tick writes through to, and
 * `firstPulledAt` is the only home the carry counter has.
 */
export interface Focus {
  id: string;
  text: string;
  done: boolean;
  ticketKey?: string;
  /** Linked brain project — lights Overview Active today (WP-C / WP-G). */
  projectId?: string;
  /** Optional issue within the project's Jira scope. */
  issueKey?: string;
  /** Brain action-register row id — the write-through target for a tick. */
  actionId?: string;
  /** What the rail shows as the ref: a Jira key, a register ref, or nothing. */
  registerRef?: string;
  /** YYYY-MM-DD first pulled. Carry age = today − firstPulledAt. */
  firstPulledAt?: string;
  /** P1/P2/P3 at pull time — the rail shows a pill only for P1. */
  priority?: string;
  /** How yesterday's pin was reconciled; anything set means it is finished. */
  resolvedAs?: "done" | "carry" | "return" | "park";
  /** YYYY-MM-DD a park was recorded — off Today, still open on the project. */
  parkedSince?: string;
}

export interface YesterdayBrief {
  date: string;
  focuses: Focus[];
  recap?: string;
}

/** An undone focus from a day before yesterday, still outstanding. */
export type CarriedFocus = Focus & { date: string };

/** A single ticket movement captured for a closed standup day. */
export interface TicketDiff {
  key: string;
  summary: string;
  movedToDone?: boolean;
  newlyBlocked?: boolean;
  statusChanged?: boolean;
  fromStatus?: string;
  toStatus?: string;
}

export interface BoardMember {
  userId: string;
  displayName: string;
  jiraAccountId?: string;
  kind?: "brain" | string;
  focuses?: Focus[];
  yesterday?: YesterdayBrief | null;
  /** Undone focuses from days before yesterday — resurface until ticked. */
  carried?: CarriedFocus[];
  closed?: boolean;
  recap?: string;
  ticketDiff?: TicketDiff[];
}

export interface StandupBoard {
  date: string;
  members: BoardMember[];
  teamDigest?: string;
}

/** One row in the standup history list (newest-first). */
export interface StandupHistoryEntry {
  date: string;
  teamDigest?: string;
}

export async function getStandupHistory(signal?: AbortSignal): Promise<StandupHistoryEntry[]> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/history`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Standup history error: ${res.status}`);
  const data = await res.json() as { standups?: StandupHistoryEntry[] };
  return data.standups ?? [];
}

export interface StandupTicket {
  key: string;
  summary: string;
  status: string;
  statusCategory: string;
  priority?: string;     // Jira priority name (e.g. "Highest")
  priorityId?: string;   // Jira priority id — present once the backend ships it
  customer?: string;     // Organizations (support tickets) / Account (issues) — customer the ticket is for
  url: string;
  blocked: boolean;
  /** True = customer support ticket (TICKET project, non-internal type); false =
   *  internal issue. The per-person "Most important" cards show tickets only. */
  isTicket: boolean;
  /** Deterministic priority score + bucket + reasons (open tickets only). */
  score?: number;
  bucket?: "P1" | "P2" | "P3";
  reasons?: string[];
}

export interface StandupTickets {
  unmapped?: boolean;
  jiraAccountId?: string;
  active: StandupTicket[];
  blocked: StandupTicket[];
  done: StandupTicket[];
  fetchedAt: number;
}

export async function getStandupBoard(date?: string, signal?: AbortSignal): Promise<StandupBoard> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const q = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(`${apiUrl}/standup/board${q}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Standup board error: ${res.status}`);
  return res.json() as Promise<StandupBoard>;
}

export async function getStandupTickets(userId: string, signal?: AbortSignal): Promise<StandupTickets> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/tickets/${encodeURIComponent(userId)}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Standup tickets error: ${res.status}`);
  return res.json() as Promise<StandupTickets>;
}

// ---------------------------------------------------------------------------
// Standup Jira write — status (transition-aware) + priority. Any signed-in user.
// ---------------------------------------------------------------------------

/** A workflow transition available on a ticket (the valid next statuses). */
export interface JiraTransition {
  id: string;
  name: string;
  toStatus: string;
}

/** A standard Jira priority option. */
export interface JiraPriority {
  id: string;
  name: string;
}

/** Valid next-status transitions for a ticket (lazy, on status-dropdown open). */
export async function getTicketTransitions(key: string, signal?: AbortSignal): Promise<JiraTransition[]> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/ticket/${encodeURIComponent(key)}/transitions`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  const body = await res.json().catch(() => null) as { transitions?: JiraTransition[]; error?: string } | null;
  if (!res.ok) throw new Error(body?.error ?? `Transitions error: ${res.status}`);
  return body?.transitions ?? [];
}

/** The standard Jira priority list (cacheable). */
export async function getPriorities(signal?: AbortSignal): Promise<JiraPriority[]> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/priorities`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  const body = await res.json().catch(() => null) as { priorities?: JiraPriority[]; error?: string } | null;
  if (!res.ok) throw new Error(body?.error ?? `Priorities error: ${res.status}`);
  return body?.priorities ?? [];
}

/** Apply a workflow transition. Returns the resulting status name. */
export async function setTicketTransition(key: string, transitionId: string, signal?: AbortSignal): Promise<string> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/ticket/${encodeURIComponent(key)}/transition`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ transitionId }),
    signal,
  });
  const body = await res.json().catch(() => null) as { ok?: boolean; status?: string; error?: string } | null;
  if (!res.ok || !body?.ok) throw new Error(body?.error ?? `Transition error: ${res.status}`);
  return body.status ?? "";
}

/** Set a ticket's priority. Returns the resulting priority name. */
export async function setTicketPriority(key: string, priorityId: string, signal?: AbortSignal): Promise<string> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/ticket/${encodeURIComponent(key)}/priority`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ priorityId }),
    signal,
  });
  const body = await res.json().catch(() => null) as { ok?: boolean; priority?: string; error?: string } | null;
  if (!res.ok || !body?.ok) throw new Error(body?.error ?? `Priority error: ${res.status}`);
  return body.priority ?? "";
}

// ---------------------------------------------------------------------------
// Standup notes (personal, rolling backlog — raised at next standup)
// ---------------------------------------------------------------------------

export interface StandupNote {
  id: string;
  text: string;
  kind: "note" | "question";
  status: "open" | "raised";
  createdAt: number;
  raisedAt?: number;
  answerKey?: string;
}

/** Open notes for a user (default: me) for the next standup, newest-first. */
export async function getStandupNotes(userId?: string, signal?: AbortSignal): Promise<StandupNote[]> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const qs = userId ? `?userId=${encodeURIComponent(userId)}` : "";
  const res = await fetch(`${apiUrl}/standup/notes${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Standup notes error: ${res.status}`);
  const data = await res.json() as { notes?: StandupNote[] };
  return data.notes ?? [];
}

/** Mic dictation: short base64 audio clip → plain transcript text (Gemini). */
export async function transcribeAudio(audio: string, mimeType: string): Promise<string> {
  const token = await getIdToken();
  const res = await fetch(`${getApiUrl()}/transcribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ audio, mimeType }),
  });
  const body = await res.json().catch(() => null) as { text?: string; error?: string } | null;
  if (!res.ok || typeof body?.text !== "string") throw new Error(body?.error ?? `Transcribe error: ${res.status}`);
  return body.text;
}

export async function addStandupNote(
  text: string,
  kind: "note" | "question",
  signal?: AbortSignal
): Promise<StandupNote> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/notes`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ text, kind }),
    signal,
  });
  const body = await res.json().catch(() => null) as { ok?: boolean; note?: StandupNote; error?: string } | null;
  if (!res.ok || !body?.ok || !body.note) throw new Error(body?.error ?? `Add note error: ${res.status}`);
  return body.note;
}

/** Mark a note raised; for a question, an optional answer is captured to the brain. */
export async function raiseStandupNote(
  id: string,
  answer?: string,
  userId?: string,
  signal?: AbortSignal
): Promise<StandupNote> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/notes/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ status: "raised", ...(answer ? { answer } : {}), ...(userId ? { userId } : {}) }),
    signal,
  });
  const body = await res.json().catch(() => null) as { ok?: boolean; note?: StandupNote; error?: string } | null;
  if (!res.ok || !body?.ok || !body.note) throw new Error(body?.error ?? `Raise note error: ${res.status}`);
  return body.note;
}

export async function deleteStandupNote(id: string, userId?: string, signal?: AbortSignal): Promise<void> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const qs = userId ? `?userId=${encodeURIComponent(userId)}` : "";
  const res = await fetch(`${apiUrl}/standup/notes/${encodeURIComponent(id)}${qs}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  const body = await res.json().catch(() => null) as { ok?: boolean; error?: string } | null;
  if (!res.ok || !body?.ok) throw new Error(body?.error ?? `Delete note error: ${res.status}`);
}

// ---------------------------------------------------------------------------
// Standup urgent tickets (SLA board)
// ---------------------------------------------------------------------------

export type SlaTier = "imminent" | "at-risk" | "on-track" | "breached" | "no-data";

export interface SlaState {
  breached: boolean;
  paused?: boolean;
  remainingMs?: number;
  goalMs?: number;
}

export interface TicketWatchDeadline {
  date: string; // YYYY-MM-DD
  quote: string; // verbatim supporting text from the ticket
}

export interface TicketWatchDigestItem {
  key: string;
  summary: string;
  line: string; // one-line AI summary
  url: string;
  created: string;
}

export interface TicketDraft {
  key: string;
  canAnswer: "yes" | "partial" | "no";
  draft: string;
  confidence: number; // 0..1
  missing?: string;
  sources: string[];
  generatedAt: number;
}

/** One "get to this today" item from the nightly priorities brief. */
export interface TicketWatchBriefItem {
  key: string;
  action: "reply" | "action";
  why: string;
}

export interface UrgentTicket {
  key: string;
  summary: string;
  status: string;
  url: string;
  assigneeAccountId?: string;
  assigneeName?: string;
  response?: SlaState;
  resolution?: SlaState;
  tier: SlaTier;
  stuckInEng: boolean;
  /** True when the ball is in our court (we owe the response), false = with client.
   *  Present once the backend ships it; treated as "our reply" when absent. */
  inOurCourt?: boolean;
  /** Short description snippet for the row-expand. */
  description?: string;
  /** Hard date commitment (text-extracted or Jira duedate). Present once the backend ships it. */
  deadline?: TicketWatchDeadline;
  /** Customer had the last word and is waiting on our reply (nightly comment scan). */
  awaiting?: { since: string; days: number };
  /** 1-2 factual sentences: what the ticket is + where it stands (nightly AI). */
  gist?: string;
  /** One imperative line: the next concrete action for our team (nightly AI). */
  next?: string;
  /** Deterministic priority score (higher = more urgent), bucket, and the
   *  ordered reasons that produced it (the tunable scoring engine). */
  score?: number;
  bucket?: "P1" | "P2" | "P3";
  reasons?: string[];
  /** Customer / org (Account field). */
  account?: string;

  // --- desk heatmap (D1 Stage 2) ---
  /** The heatmap's Y axis: which of the six desk states this ticket sits in. */
  deskState?: DeskState;
  /** ISO timestamp of the last REAL activity (ball flip / comment / transition). */
  lastActivityAt?: string;
  lastActivityKind?: "awaiting" | "comment" | "transition" | "created";
  /** Whole days since `lastActivityAt` — the Age axis. NOT days since created. */
  idleDays?: number;
  created?: string;
  /** JSM Organization, else the Account field. */
  customer?: string;
  priorityName?: string;
  /** The triage agent's latest verdict, compacted (D3). Absent = untriaged. */
  triage?: TriageSummary;
}

/** The six desk states — the heatmap's Y axis, in two bands. */
export type DeskState =
  | "needs_reply"
  | "needs_decision"
  | "untriaged"
  | "with_developers"
  | "waiting_on_customer"
  | "ready_to_close";

export type TriageDisposition =
  | "answer"
  | "raise_issue"
  | "reproduce_first"
  | "scope_or_split"
  | "link_existing"
  | "close_superseded"
  | "nudge_customer"
  | "resolve_contradiction";

/** The compact verdict summary carried on each ticket (D3). */
export interface TriageSummary {
  ticketKey: string;
  verdictId: string;
  state: DeskState;
  bucket: "A" | "B" | "C" | "D";
  disposition: TriageDisposition;
  lane: string;
  module: string;
  recommendation: string;
  confidence: number;
  flags: string[];
  stale: boolean;
  generatedAt: number;
}

/** The pulse line + the day-over-day baseline for the heatmap delta column. */
export interface DeskSummary {
  withUs: number;
  untriaged: number;
  triagedPendingReview: number;
  medianFirstResponseMs?: number;
  closedYesterday?: number;
  /** null = no baseline yet; the delta column then shows `—`, never a fake 0. */
  deltas: Partial<Record<DeskState, number>> | null;
  baselineDate?: string;
  /** Epoch ms the baseline snapshot was frozen. It is written on the FIRST open
   *  of its day, so `Δ 1D` can cover anywhere from a few hours to a full day —
   *  the tab prints this rather than letting the label imply 24h. */
  baselineAt?: number;
  discussed: string[];
}

export interface UrgentPerson {
  displayName: string;
  accountId?: string;
  top: UrgentTicket[];
}

export interface UrgentResult {
  date: string;
  scoreboard: {
    open: number;
    breached: number;
    breachedActive: number;
    breachedEng: number;
    imminent: number;
    atRisk: number;
    onTrack: number;
    noData: number;
    /** Tickets whose response SLA is running and the ball is in our court.
     *  Present once the backend ships it. */
    ourReplyDue?: number;
    /** Tickets where the customer had the last word (ticket-watch comment scan). */
    awaitingReply?: number;
    /** Ticket counts by priority-score bucket (at-a-glance summary). */
    p1?: number;
    p2?: number;
    p3?: number;
  };
  perPerson: UrgentPerson[];
  imminent: UrgentTicket[];
  atRisk: UrgentTicket[];
  breachedActive: UrgentTicket[];
  stuckInEng: UrgentTicket[];
  /** Flat, worst-first urgent set (breached + imminent + at-risk) for client-side
   *  filtering. Present once the backend ships it; falls back to the section lists. */
  urgent?: UrgentTicket[];
  /** EVERY open ticket, worst-first — the "All open" audit sweep. */
  tickets?: UrgentTicket[];
  /** Deadline-bearing tickets within the horizon — pinned top, ignores stuck-in-eng. */
  dateCommitments?: UrgentTicket[];
  /** Every ticket the customer is waiting on — longest wait first, never
   *  maskable by status (nothing falls through the net). */
  needsReply?: UrgentTicket[];
  /** Daily "most important tickets to get to" brief from the nightly run. */
  brief?: TicketWatchBriefItem[];
  /** Overnight new-ticket digest from the nightly ticket-watch run. */
  digest?: TicketWatchDigestItem[];
  /** AI draft answers keyed by issue key — human review only, never auto-sent. */
  drafts?: Record<string, TicketDraft>;
  /** When the nightly ticket-watch row backing brief/needsReply was generated. */
  watchGeneratedAt?: number;
  /** Everything the desk heatmap needs that isn't derivable from `tickets[]`. */
  desk?: DeskSummary;
  /** True when the open-ticket sweep hit the page cap — the heatmap must say so
   *  rather than present a partial desk as the whole desk. */
  truncated?: boolean;
  fetchedAt: number;
  error?: string;
}

export async function getStandupUrgent(date?: string, signal?: AbortSignal): Promise<UrgentResult> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const q = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(`${apiUrl}/standup/urgent${q}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Standup urgent error: ${res.status}`);
  return res.json() as Promise<UrgentResult>;
}

// --- Ticket drawer (D1 Stage 2) ---------------------------------------------

export type ThreadRole = "customer" | "aeronet" | "internal";

export interface DeskThreadMessage {
  id: string;
  name: string;
  role: ThreadRole;
  at: string;
  text: string;
}

export interface DeskLinkedIssue {
  key: string;
  summary: string;
  status: string;
  statusCategory: string;
  url: string;
  fixVersions: string[];
  coversRelease: boolean;
  /** The Jira link phrase as written (`is escalated to`, `is duplicated by`, …).
   *  The section lists EVERY link, so it has to say what kind each one is. */
  linkType?: string;
}

/** The stored triage verdict, in full (the drawer's agent note). */
export interface TriageVerdict {
  ticketKey: string;
  verdictId: string;
  policyVersion: number;
  state: DeskState;
  score: number;
  scoreBucket: "P1" | "P2" | "P3";
  scoreReasons: string[];
  bucket: "A" | "B" | "C" | "D";
  disposition: TriageDisposition;
  routing: { lane: string; module: string; reason?: string };
  suggestedAssignee?: string;
  recommendation: string;
  draft?: { kind: "reply" | "issue" | "none"; text: string; confidence: number };
  reasons: string[];
  flags: string[];
  contextRefs: string[];
  confidence: number;
  generatedAt: number;
  model: string;
}

export interface DeskTicketDetail {
  key: string;
  summary: string;
  url: string;
  status: string;
  priorityName?: string;
  /** The scoring engine's live P1/P2/P3 bucket — same number as the heatmap row. */
  scoreBucket?: "P1" | "P2" | "P3";
  score?: number;
  scoreReasons?: string[];
  customer?: string;
  reporter?: string;
  reporterOrg?: string;
  assigneeName?: string;
  created?: string;
  epic?: string;
  fixVersions: string[];
  releaseLabels: string[];
  deskState: DeskState;
  ballWithUs: boolean;
  idleDays: number;
  lastActivityAt: string;
  lastActivityKind: "awaiting" | "comment" | "transition" | "created";
  description?: string;
  thread: DeskThreadMessage[];
  linked: DeskLinkedIssue[];
  triage?: TriageVerdict;
  triageStale?: boolean;
  discussed?: { at: number; by: string };
}

export async function getTicketDesk(
  key: string,
  date?: string,
  signal?: AbortSignal
): Promise<DeskTicketDetail> {
  const token = await getIdToken();
  const q = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(
    `${getApiUrl()}/standup/ticket/${encodeURIComponent(key)}/desk${q}`,
    { headers: { Authorization: `Bearer ${token}` }, signal }
  );
  if (!res.ok) throw new Error(`Ticket desk error: ${res.status}`);
  return res.json() as Promise<DeskTicketDetail>;
}

export type DeskNoteResponse =
  | { ok: true; id: string; created: string; internal: boolean; text: string; logged: boolean }
  | { ok: false; error: string };

/**
 * Post the composer's note. `mode` is REQUIRED and sent verbatim — there is no
 * client-side default, so a bug here can never silently email a customer.
 */
export async function postDeskNote(
  key: string,
  body: { text: string; mode: "internal" | "customer"; standup?: { date: string; attendees?: string[] } }
): Promise<DeskNoteResponse> {
  const token = await getIdToken();
  const res = await fetch(`${getApiUrl()}/standup/ticket/${encodeURIComponent(key)}/desk-note`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.json() as Promise<DeskNoteResponse>;
}

export async function postTicketDiscussed(
  key: string,
  discussed: boolean,
  date?: string
): Promise<{ ok: boolean; discussed?: boolean; error?: string }> {
  const token = await getIdToken();
  const res = await fetch(`${getApiUrl()}/standup/ticket/${encodeURIComponent(key)}/discussed`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ discussed, ...(date ? { date } : {}) }),
  });
  return res.json() as Promise<{ ok: boolean; discussed?: boolean; error?: string }>;
}

/** Kick off a manual ticket-watch run (async — results land in a few minutes). */
export async function runTicketWatch(): Promise<void> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/ticket-watch/run`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Ticket watch run error: ${res.status}`);
}

// --- Ticket priority scoring config (admin-tunable weights) ----------------

export interface ScoringConfig {
  weights: Record<string, number>;
  waitingDampen: number;
  slaApproachFraction: number;
  slaApproachMillis: number;
  bucketP1: number;
  bucketP2: number;
  developerName: string;
  waitingOnCustomer: string[];
  unownedAssignees: string[];
}

export async function getScoringConfig(signal?: AbortSignal): Promise<ScoringConfig> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/ticket-scoring/config`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Scoring config error: ${res.status}`);
  return res.json() as Promise<ScoringConfig>;
}

/** Save the scoring weights (admin only — 403 otherwise). Returns the stored config. */
export async function putScoringConfig(config: ScoringConfig): Promise<ScoringConfig> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/ticket-scoring/config`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ config }),
  });
  if (!res.ok) throw new Error(res.status === 403 ? "Admin role required to edit scoring." : `Save scoring error: ${res.status}`);
  return res.json() as Promise<ScoringConfig>;
}

export async function putStandupFocuses(
  userId: string,
  date: string,
  focuses: Focus[],
  signal?: AbortSignal
): Promise<Focus[]> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/focuses`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ userId, date, focuses }),
    signal,
  });
  const body = await res.json().catch(() => null) as { ok?: boolean; focuses?: Focus[]; error?: string } | null;
  if (!res.ok || !body?.ok) throw new Error(body?.error ?? `Save focuses error: ${res.status}`);
  return body.focuses ?? focuses;
}

/** Tick/untick one focus on its ORIGINAL row (yesterday/carried included). */
export async function setFocusDone(
  userId: string,
  date: string,
  id: string,
  done: boolean
): Promise<Focus[]> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/focus-done`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ userId, date, id, done }),
  });
  const body = await res.json().catch(() => null) as { ok?: boolean; focuses?: Focus[]; error?: string } | null;
  if (!res.ok || !body?.ok) throw new Error(body?.error ?? `Focus done error: ${res.status}`);
  return body.focuses ?? [];
}

/** Person-tab project deltas + focus-composer quick-picks (WP-C). */
export interface PersonProjectDelta {
  projectId: string;
  name: string;
  delta: string;
  doneSince: number;
  newBlockers: number;
  planAwaiting?: string;
  primary?: boolean;
}

export interface StandupPersonDeltas {
  userId: string;
  date: string;
  since: string;
  personSlug?: string;
  primaryProjectId?: string;
  deltas: PersonProjectDelta[];
  projects: { projectId: string; name: string; primary?: boolean }[];
}

export async function getStandupPersonDeltas(
  userId: string,
  date?: string,
  signal?: AbortSignal
): Promise<StandupPersonDeltas> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const q = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(`${apiUrl}/standup/person-deltas/${encodeURIComponent(userId)}${q}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Person deltas error: ${res.status}`);
  return res.json() as Promise<StandupPersonDeltas>;
}

/** Run-standup project rail (design 2026-08-06). */
export interface RunIssue {
  key: string;
  summary: string;
  status: string;
  statusCategory: string;
  priority: string | null;
  blocked: boolean;
  url: string;
}

export type YesterdayRow = Focus & { date: string; carriedDays: number };

export type RunTab =
  | { kind: "yesterday"; badge: number; rows: YesterdayRow[] }
  | {
      kind: "project";
      projectId: string;
      name: string;
      description?: string;
      customer?: string;
      owner?: string;
      status?: string;
      pinned: boolean;
      badge: number;
      rollups: { yoursOpen: number; blocked: number; doneSince: number };
      delta: string;
      issues: RunIssue[];
    }
  | { kind: "unassigned"; badge: number; rows: YesterdayRow[] };

export interface RunRail {
  userId: string;
  date: string;
  /** personSlug ?? userId — Activity.actor on commit. */
  actor?: string;
  jiraAccountId?: string;
  tabs: RunTab[];
}

export async function getStandupRun(
  userId: string,
  date?: string,
  signal?: AbortSignal
): Promise<RunRail> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const q = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(`${apiUrl}/standup/run/${encodeURIComponent(userId)}${q}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Standup run error: ${res.status}`);
  return res.json() as Promise<RunRail>;
}

/** Pin projects onto a person's run rail (edit-own-or-admin). */
export async function putStandupRunPins(
  userId: string,
  pinnedProjectIds: string[]
): Promise<void> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/run/${encodeURIComponent(userId)}/pins`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ pinnedProjectIds }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? `Standup pins error: ${res.status}`);
  }
}

export async function closeStandup(date: string): Promise<{ ok: boolean; entriesClosed?: number; doc_id?: string }> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/standup/close`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ date }),
  });
  const body = await res.json().catch(() => null) as { ok?: boolean; entriesClosed?: number; doc_id?: string; error?: string } | null;
  if (!res.ok || !body?.ok) throw new Error(body?.error ?? `Close standup error: ${res.status}`);
  return { ok: true, entriesClosed: body.entriesClosed, doc_id: body.doc_id };
}

// ---------------------------------------------------------------------------
// Brain Health dashboard — company knowledge health aggregate (GET /brain/health)
// ---------------------------------------------------------------------------

export interface BrainHealthResponse {
  score: number;
  scoreDelta: number;
  totals: { facts: number; pages: number };
  coveragePct: number;
  coverageDelta: number;
  openQuestions: number;
  contradictions: { open: number; resolvedAllTime: number };
  series: {
    knowledgeGrowth: number[];
    ingestionPerDay: number[];
    contradictions: { detected: number; resolved: number }[];
    sparklines: { knowledge: number[]; coverage: number[]; questions: number[]; contradictions: number[] };
  };
  weather: { fresh: number; aging: number; unknown: number; atRisk: number };
  freshness: { under30d: number; d30to90: number; stale: number };
  coverageByArea: { area: string; pct: number }[];
  sources: { name: string; icon: string; status: "ok" | "lagging" | "off"; lastSync: string }[];
  contributors: { name: string; initials: string; isAgent: boolean; count: number }[];
  openContradictions: { title: string; detail: string; severity: "red" | "amber"; ageDays: number }[];
}

/** The whole Brain Health dashboard, computed live server-side for the period. */
export async function getBrainHealth(period: string, signal?: AbortSignal): Promise<BrainHealthResponse> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/brain/health?period=${encodeURIComponent(period)}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Brain health error: ${res.status}`);
  return res.json() as Promise<BrainHealthResponse>;
}

export interface JiraUserOption {
  accountId: string;
  displayName: string;
  emailAddress?: string;
  avatarUrl?: string;
}

/** Admin-only: assignable Jira users for the mapping dropdown. */
export async function listJiraUsers(query = "", signal?: AbortSignal): Promise<JiraUserOption[]> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const q = query ? `?query=${encodeURIComponent(query)}` : "";
  const res = await fetch(`${apiUrl}/admin/jira/users${q}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? `Jira users error: ${res.status}`);
  }
  const data = await res.json() as { users: JiraUserOption[] };
  return data.users ?? [];
}

export interface SlackInstallation {
  teamId: string;
  teamName: string;
  slackUserId: string;
  installedAt: string;
}

export async function getSlackInstallUrl(): Promise<string> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/slack/install`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Slack install error: ${res.status}`);
  const data = await res.json() as { url: string };
  return data.url;
}

export async function connectSlackCallback(
  code: string,
  state: string
): Promise<{ ok: boolean; teamName: string; dmSent: boolean }> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/slack/callback`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? `Slack callback error: ${res.status}`);
  }
  return res.json() as Promise<{ ok: boolean; teamName: string; dmSent: boolean }>;
}

export async function getSlackInstallations(): Promise<SlackInstallation[]> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/slack/installations`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Slack installations error: ${res.status}`);
  const data = await res.json() as { installations: SlackInstallation[] };
  return data.installations ?? [];
}

export interface GoogleConnection {
  email: string;
  connectedAt: string;
}

export async function getGoogleConnectUrl(): Promise<string> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/google/connect`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Google connect error: ${res.status}`);
  const data = await res.json() as { url: string };
  return data.url;
}

export async function connectGoogleCallback(
  code: string,
  state: string
): Promise<{ ok: boolean; email: string }> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/google/callback`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? `Google callback error: ${res.status}`);
  }
  return res.json() as Promise<{ ok: boolean; email: string }>;
}

export async function getGoogleConnections(): Promise<GoogleConnection[]> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/google/connections`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Google connections error: ${res.status}`);
  const data = await res.json() as { connections?: Array<{ email: string; connectedAt: string } & Record<string, unknown>> };
  return (data.connections ?? []).map(({ email, connectedAt }) => ({ email, connectedAt }));
}

export async function disconnectGoogleConnection(email: string): Promise<void> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/google/connections`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    let message = `Google disconnect error: ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body && typeof body.error === "string") message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
}

export async function syncGmail(
  email: string
): Promise<{ captured: number; skipped: number }> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/google/sync`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? `Gmail sync error: ${res.status}`);
  }
  return res.json() as Promise<{ captured: number; skipped: number }>;
}

export async function disconnectSlackInstallation(teamId: string): Promise<void> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/slack/installations/${encodeURIComponent(teamId)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let message = `Slack disconnect error: ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body && typeof body.error === "string") message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
}

export async function deleteAccount(): Promise<void> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}/user`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? `Delete account error: ${res.status}`);
  }
}


// ---------------------------------------------------------------------------
// Person tab — projects, pins and the morning reconciliation (D1-c)
// ---------------------------------------------------------------------------

export type MovementTone = "success" | "warning" | "danger" | "none";

/** Snapshot-derived movement. `line: ""` means SHOW NOTHING — deliberately. */
export interface Movement {
  known: boolean;
  actionsDone: number;
  jiraMoved: number;
  created: number;
  quietDays: number;
  line: string;
  tone: MovementTone;
  reason?: "no-history" | "under-floor";
}

export type MembershipKind = "owner" | "working" | "support";

export interface PersonProjectCard {
  projectId: string;
  name: string;
  kind: MembershipKind;
  customer?: string;
  owner?: string;
  status?: string;
  blocked?: boolean;
  /** Ring: % of THIS PERSON'S slice that is done, not the project's. */
  pct: number;
  /** Whose figure `pct` is — falls back to `project` when they hold no rows. */
  pctScope?: "person" | "project";
  counts: { open: number; yours: number; yoursDone?: number; blocked: number; done: number };
  movement: Movement;
  meta: string;
  bands: string[];
  /** In-flight row holders, owner excluded — drawn as avatars on the closed card. */
  assignees: string[];
  /** On this person's focus list. */
  focused: boolean;
}

/** A pin taken out of Today but still open on its project. */
export interface ParkedPin {
  focusId: string;
  date: string;
  parkedSince: string;
  parkedDays: number;
  text: string;
  projectId?: string;
  issueKey?: string;
  actionId?: string;
  ref?: string;
}

export interface PersonProjects {
  userId: string;
  date: string;
  personSlug?: string;
  displayName: string;
  jiraAccountId?: string;
  owner: PersonProjectCard[];
  working: PersonProjectCard[];
  support: PersonProjectCard | null;
  parked: ParkedPin[];
  /** This person's focus list — the same profile field the run rail pins with. */
  pinnedProjectIds: string[];
  snapshotReady: boolean;
}

/** The person tab: derived membership + movement + parked pins. */
export async function getPersonProjects(
  userId: string,
  date?: string,
  signal?: AbortSignal,
): Promise<PersonProjects> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const q = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await fetch(`${apiUrl}/standup/person-projects/${encodeURIComponent(userId)}${q}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Person projects error: ${res.status}`);
  return res.json() as Promise<PersonProjects>;
}

/** The four reconciliation outcomes. `park` is Isaac's addition to the handoff. */
export type FocusResolution = "done" | "carry" | "return" | "park";

export interface ReconcileResponse {
  ok: boolean;
  error?: string;
  focuses?: Focus[];
  /** The pin resolved locally but Jira was NOT updated — the UI must say so. */
  warning?: string;
}

async function postStandupJson(path: string, body: unknown): Promise<ReconcileResponse> {
  const token = await getIdToken();
  const apiUrl = getApiUrl();
  const res = await fetch(`${apiUrl}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const parsed = (await res.json().catch(() => null)) as ReconcileResponse | null;
  if (!res.ok || !parsed?.ok) throw new Error(parsed?.error ?? `Request failed: ${res.status}`);
  return parsed;
}

/** Resolve one of yesterday's pins: done | carry | return | park. */
export async function reconcilePin(input: {
  userId: string;
  date: string;
  fromDate: string;
  focusId: string;
  resolution: FocusResolution;
}): Promise<ReconcileResponse> {
  return postStandupJson("/standup/reconcile", input);
}

/**
 * Tick a pin that is already in Today. A pin is not a copy — this writes through
 * to the register row it names, and warns when there is no Jira transition to make.
 */
export async function setPinDone(input: {
  userId: string;
  date: string;
  focusId: string;
  done: boolean;
}): Promise<ReconcileResponse> {
  return postStandupJson("/standup/pin-done", input);
}
